import { useState } from 'react';
import { useProjectRework } from '../hooks/useReworkQueue';
import QuickReworkModal from './QuickReworkModal';
import { formatDistanceToNow } from 'date-fns';

/**
 * Project Rework Section - v2.1
 *
 * Displays rework events for a project.
 * Used in Project Detail page.
 *
 * Props:
 * - quoteNumber: string - Project/quote number
 * - projectName?: string - Project name for context
 * - context?: object - Additional context for creating new events
 */
export default function ProjectReworkSection({ quoteNumber, projectName, context = {} }) {
  const { events, loading, error, stats, refetch } = useProjectRework(quoteNumber);
  const [showReportModal, setShowReportModal] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);

  if (loading && events.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Rework</h3>
        </div>
        <div className="text-center py-8 text-gray-500">Loading rework data...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Rework</h3>
          {stats.totalHours > 0 && (
            <span className="text-sm text-gray-500">
              Total: {stats.totalHours} hrs
            </span>
          )}
        </div>
        <button
          onClick={() => setShowReportModal(true)}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
        >
          Report Issue
        </button>
      </div>

      {/* Stats Bar (if there are open events) */}
      {stats.openCount > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-4 text-sm">
          <span className="font-medium text-amber-800">
            {stats.openCount} open {stats.openCount === 1 ? 'issue' : 'issues'}
          </span>
        </div>
      )}

      {/* Events List */}
      <div className="divide-y divide-gray-100">
        {events.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No rework events for this project.
          </div>
        ) : (
          events.map((event) => (
            <ReworkEventRow
              key={event.id}
              event={event}
              expanded={expandedEventId === event.id}
              onToggle={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
            />
          ))
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 text-red-700 text-sm">
          Error loading rework data: {error}
        </div>
      )}

      {/* Quick Report Modal */}
      <QuickReworkModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={() => {
          setShowReportModal(false);
          refetch();
        }}
        context={{
          quoteNumber,
          projectName,
          ...context,
        }}
      />
    </div>
  );
}

/**
 * Single rework event row with expandable details.
 */
function ReworkEventRow({ event, expanded, onToggle }) {
  const statusColors = {
    open: 'bg-amber-100 text-amber-800',
    resolved: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-600',
  };

  const severityColors = {
    scrap: 'bg-red-500 text-white',
    requires_rework: 'bg-amber-500 text-white',
    cosmetic: 'bg-gray-400 text-white',
  };

  const statusColor = statusColors[event.status] || statusColors.open;
  const severityColor = severityColors[event.severity] || severityColors.requires_rework;

  const ageText = event.createdAt
    ? formatDistanceToNow(new Date(event.createdAt), { addSuffix: false })
    : '';

  return (
    <div className={`${event.status === 'open' ? 'bg-amber-50/30' : ''}`}>
      {/* Main Row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        {/* Status Indicator */}
        <div className={`w-2 h-2 rounded-full ${event.status === 'open' ? 'bg-amber-500' : 'bg-green-500'}`} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium uppercase ${statusColor}`}>
              {event.status}
            </span>
            {event.severity === 'scrap' && (
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium uppercase ${severityColor}`}>
                SCRAP
              </span>
            )}
            <span className="text-sm font-medium text-gray-900">
              {event.issueLabel || event.issueType}
            </span>
            <span className="text-xs text-gray-400">
              {ageText}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            SKU: {event.jobNumber}
            {event.discoveredWorkCenter && (
              <span className="ml-2 text-gray-400">• Found at {event.discoveredWorkCenter}</span>
            )}
          </div>
        </div>

        {/* Time Badge (if resolved) */}
        {event.totalReworkSeconds > 0 && (
          <span className="text-sm text-gray-500">
            {(event.totalReworkSeconds / 3600).toFixed(1)}h
          </span>
        )}

        {/* Expand Arrow */}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pl-9 space-y-3">
          {/* Description */}
          {event.description && (
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">Description:</span> {event.description}
            </div>
          )}

          {/* Work Center Progress */}
          {event.reworkRequired && event.reworkRequired.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-700 font-medium">Progress:</span>
              {event.reworkRequired.map((rr) => (
                <span
                  key={rr.workCenter}
                  className={`px-2 py-0.5 rounded ${
                    rr.required
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {rr.workCenter} {rr.required ? '⏳' : '✓'}
                </span>
              ))}
            </div>
          )}

          {/* Reporter */}
          <div className="text-sm text-gray-500">
            Reported by {event.reportedByName || 'Unknown'}
            {event.resolvedAt && (
              <span className="ml-2">
                • Resolved {formatDistanceToNow(new Date(event.resolvedAt), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
